// The Waiting in line tile's sub-line (#6708): which gate is REALLY holding the queue. Pure
// data; `FleetHealthTiles` translates it. Before this the tile said "Next starts when a
// machine is free" whenever anything waited, and on 2026-10-20 it said so while four of six
// machines idled behind key-saturated work.
//
// It reads the api's `queue` (a projection of the stall report; `ready + held` is the lane
// sum the tile counts) and takes the FIRST matching branch:
//
//   - no `queue` (a caller without `read:tasks`, or an api older than this bundle): today's
//     copy, by `queued` alone. It cannot tell ready work from held, so it claims neither;
//   - `held > 0`: the LEAD reason names it. The lead is the first `top` entry that is NOT
//     `blocked_on_answer`, whose count can be asks no queued task carries and so are not
//     queue rows. `{n}` is `min(lead.tasks, held)`, never more than the tile counts, and
//     the full `held` when there is no lead. An empty `top` (the report and the lanes are
//     two reads, so a claim between them can leave held rows the report did not classify)
//     reads the neutral "{n} held";
//   - `held = 0` with ready work: "Dispatching…" when a machine is IDLE (`FleetTiles.busy
//     .idle > 0`: its verdict status is `idle`), today's copy when none is;
//   - otherwise empty.
//
// "READY WORK" IS THE READY WORK THE TILE COUNTS: `min(ready, queued)`. The same two-read
// race can leave the report counting ready work the lanes no longer hold; the sub-line then
// follows the tile ("Machines are ready for more work" beside "0 tasks"), never
// "Dispatching…" beside it.

import { TaskStallKind } from '@developerz/domain';
import type { UtilQueue, UtilQueueReason } from './fleetUtilization';

/** The `fleet.top.tiles.held_reason.*` label for every stall kind: typecheck enforces totality. */
export const HELD_REASON_KEY: Readonly<Record<TaskStallKind, string>> = {
  dependency_failed: 'fleet.top.tiles.held_reason.dependency_failed',
  blocked_on_answer: 'fleet.top.tiles.held_reason.blocked_on_answer',
  upstream_group_unmerged: 'fleet.top.tiles.held_reason.upstream_group_unmerged',
  upstream_leg_undeployed: 'fleet.top.tiles.held_reason.upstream_leg_undeployed',
  dependency_pending: 'fleet.top.tiles.held_reason.dependency_pending',
  dependency_unreleasable: 'fleet.top.tiles.held_reason.dependency_unreleasable',
  no_box_on_account: 'fleet.top.tiles.held_reason.no_box_on_account',
  no_usable_keys: 'fleet.top.tiles.held_reason.no_usable_keys',
  area_busy: 'fleet.top.tiles.held_reason.area_busy',
  repo_not_onboarded: 'fleet.top.tiles.held_reason.repo_not_onboarded',
  no_converse_capacity: 'fleet.top.tiles.held_reason.no_converse_capacity',
  no_service_capacity: 'fleet.top.tiles.held_reason.no_service_capacity',
  seat_capped: 'fleet.top.tiles.held_reason.seat_capped',
  pool_cooling: 'fleet.top.tiles.held_reason.pool_cooling',
  credential_cooling: 'fleet.top.tiles.held_reason.credential_cooling',
  box_fault_excluded: 'fleet.top.tiles.held_reason.box_fault_excluded',
  mode_disabled: 'fleet.top.tiles.held_reason.mode_disabled',
  no_role_machine: 'fleet.top.tiles.held_reason.no_role_machine',
  repo_env_fault: 'fleet.top.tiles.held_reason.repo_env_fault',
  resume_backoff: 'fleet.top.tiles.held_reason.resume_backoff',
  key_saturated: 'fleet.top.tiles.held_reason.key_saturated',
  selection_waiting: 'fleet.top.tiles.held_reason.selection_waiting',
  pr_quiet_window: 'fleet.top.tiles.held_reason.pr_quiet_window',
  env_fault_exhausted: 'fleet.top.tiles.held_reason.env_fault_exhausted',
  plan_paused: 'fleet.top.tiles.held_reason.plan_paused',
  machine_pinned: 'fleet.top.tiles.held_reason.machine_pinned',
};

/** What the sub-line says, before translation. `n` is the held count it names. */
export type QueueLine =
  | { readonly kind: 'machine_free' }
  | { readonly kind: 'empty' }
  | { readonly kind: 'dispatching' }
  | { readonly kind: 'key_room'; readonly n: number }
  | { readonly kind: 'reserved'; readonly n: number }
  | { readonly kind: 'area_busy'; readonly n: number }
  | { readonly kind: 'held'; readonly n: number; readonly reason: TaskStallKind }
  | { readonly kind: 'held_unclassified'; readonly n: number };

/** A `key_saturated` lead waits mainly on key room; an absent split counts as key room. */
function waitsOnKeyRoom(lead: UtilQueueReason): boolean {
  if (lead.by_release === undefined) return true;
  const share = (on: string): number =>
    lead.by_release?.filter((b) => b.releases_on === on).reduce((s, b) => s + b.tasks, 0) ?? 0;
  return share('key_room') >= share('time_or_route');
}

function heldLine(queue: UtilQueue): QueueLine {
  const { held, top } = queue;
  if (top.length === 0) return { kind: 'held_unclassified', n: held };
  const lead = top.find((r) => r.kind !== TaskStallKind.BlockedOnAnswer);
  if (lead === undefined) {
    return { kind: 'held', n: held, reason: TaskStallKind.BlockedOnAnswer };
  }
  const n = Math.min(lead.tasks, held);
  if (lead.kind === TaskStallKind.KeySaturated) {
    return waitsOnKeyRoom(lead) ? { kind: 'key_room', n } : { kind: 'reserved', n };
  }
  if (lead.kind === TaskStallKind.AreaBusy) return { kind: 'area_busy', n };
  return { kind: 'held', n, reason: lead.kind };
}

/**
 * The sub-line for a tile counting `queued`, with `idle` machines idle (`FleetTiles.busy
 * .idle`). See the file header for the branch order.
 */
export function queueLine(input: {
  readonly queued: number;
  readonly idle: number;
  readonly queue: UtilQueue | null | undefined;
}): QueueLine {
  const { queued, idle, queue } = input;
  if (queue === null || queue === undefined) {
    return queued > 0 ? { kind: 'machine_free' } : { kind: 'empty' };
  }
  if (queue.held > 0) return heldLine(queue);
  if (Math.min(queue.ready, queued) > 0) {
    return idle > 0 ? { kind: 'dispatching' } : { kind: 'machine_free' };
  }
  return { kind: 'empty' };
}
